import {
  memo,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  MenuItem,
  Select,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';
import { APIService } from 'src/api/api';
import { getAllSites } from 'src/redux/siteSlice';
import { useDispatch, useSelector } from 'react-redux';
import Loader from 'src/common/Loader';
import ReactECharts from 'src/common/ReactECharts';
import ROUTE from 'src/utils/routeConstants';
import { chartInputStyles, getVehicleUtilizationChartData } from './chartConfig';
import { getVehicleUtilizationList } from './chartService';

const VehicleUtilizationChart = ({ sx, ...props }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { allSites } = useSelector((state) => state.sites);
  const [loading, setLoading] = useState(true);
  const [salesData, setSalesData] = useState(null);
  const [selectedSite, setSelectedSite] = useState('all');
  const [vehicleList, setVehicleList] = useState(null);
  const isMountedRef = useRef(false);
  const requestIdRef = useRef(0);
  const siteData = [{ id: 'all', name: 'All Sites' }, ...allSites];

  const fetchChartData = async (id) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (isMountedRef.current) {
      setLoading(true);
    }

    try {
      const siteId = id === 'all' ? '' : id;
      const res = await APIService.getVehicleUtilization(siteId);
      if (!isMountedRef.current || requestId !== requestIdRef.current) {
        return;
      }

      if (!res.success) throw res;
      setVehicleList(res.result);
      const chartOptions = getVehicleUtilizationChartData(res.result);
      setSalesData(chartOptions);
    } catch (error) {
      if (!isMountedRef.current || requestId !== requestIdRef.current) {
        return;
      }

      console.log('dashboard sales error', error.message);
    } finally {
      if (isMountedRef.current && requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    fetchChartData(selectedSite);
    dispatch(getAllSites());
  }, []);

  const handleSiteChange = (e) => {
    const siteId = e.target.value;
    setSelectedSite(siteId);
    fetchChartData(siteId);
  };

  const onChartClick = (chartData) => {
    const vehicleData = getVehicleUtilizationList(vehicleList, chartData.dataIndex);
    history.push(ROUTE.vehicleStatusList, vehicleData);
  };

  const onEvents = {
    click: onChartClick,
  };

  return (
    <Card
      {...props}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...sx
      }}
    >
      <CardHeader
        title="Vehicle Utilization"
        sx={{
          backgroundColor: '#fff',
          color: '#111827'
        }}
        action={(
          <Select
            name="site"
            variant="standard"
            onChange={handleSiteChange}
            value={selectedSite}
            sx={chartInputStyles}
          >
            {siteData.map((item) => (
              <MenuItem value={item.id} key={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </Select>
        )}
      />
      <Divider />
      <CardContent sx={{ px: 0, flex: 1 }}>
        <Box sx={{ height: 400, position: 'relative' }}>
          {loading || !salesData ? (
            <Loader />
          ) : (
            <ReactECharts options={salesData} onEvents={onEvents} />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default memo(VehicleUtilizationChart);

VehicleUtilizationChart.propTypes = {
  sx: PropTypes.object
};
